import { useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { FilterBar } from "../components/ui/FilterBar";
import { StatusChip } from "../components/ui/StatusChip";
import { OwnerTag } from "../components/ui/OwnerTag";
import { PriorityDot } from "../components/ui/PriorityDot";
import type { Card, FilterState } from "../data/types";
import { getTaskMeta } from "../lib/taskMeta";
import { groupByPhase, type TimelineTab, type PhaseBlock } from "../lib/timelineGrouping";

interface TimelinePageProps {
  cards: Card[];
  filters: FilterState;
  onFilterChange: (updates: Partial<FilterState>) => void;
  onResetFilters: () => void;
  onSelectCard: (cardId: string) => void;
}

const TAB_CONFIG: Array<{ id: TimelineTab; label: string }> = [
  { id: "one_off", label: "One-off" },
  { id: "recurring_daily", label: "Recurring Daily" },
  { id: "recurring_weekly", label: "Recurring Weekly" },
];

const PHASE_LABEL: Record<string, string> = {
  p0: "Phase 0 — Base Closure",
  p1: "Phase 1 — Pre-Launch Active",
  p2: "Phase 2 — Launch Day",
  p3: "Phase 3 — Conversion Sprint",
  p4: "Phase 4 — Beta Close Sprint",
  unknown: "Unmapped Phase",
};

const WEEK_LABEL: Record<number, string> = {
  0: "W0 Pre-launch",
  1: "W1 Launch",
  2: "W2 Optimize",
  3: "W3 Scale",
  4: "W4 Transition",
};

export function TimelinePage({ cards, filters, onFilterChange, onResetFilters, onSelectCard }: TimelinePageProps) {
  const [tab, setTab] = useState<TimelineTab>("one_off");

  const phaseBlocks = useMemo(() => groupByPhase(cards, tab), [cards, tab]);

  return (
    <div className="h-full flex flex-col">
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] space-y-[var(--spacing-3)]">
        <div className="flex items-center gap-[var(--spacing-2)]">
          {TAB_CONFIG.map((tabOption) => (
            <button
              key={tabOption.id}
              onClick={() => setTab(tabOption.id)}
              className={cn(
                "h-[30px] px-[var(--spacing-3)] rounded-[var(--radius-md)] text-[13px] font-medium cursor-pointer transition-colors duration-[var(--duration-fast)]",
                tab === tabOption.id
                  ? "bg-[var(--color-accent-subtle-bg)] text-[var(--color-accent)]"
                  : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
              )}
            >
              {tabOption.label}
            </button>
          ))}
          <span className="ml-auto text-[12px] text-[var(--color-text-muted)]">{phaseBlocks.reduce((acc, block) => acc + block.cards.length, 0)} tasks</span>
        </div>

        <FilterBar filters={filters} onChange={onFilterChange} onReset={onResetFilters} showWeekFilter className="pt-[var(--spacing-1)]" />
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="w-full px-[var(--spacing-4)] py-[var(--spacing-4)] space-y-[var(--spacing-4)]">
          {phaseBlocks.length === 0 ? (
            <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] px-[var(--spacing-4)] py-[var(--spacing-6)] text-[14px] text-[var(--color-text-muted)]">
              No tasks for this view.
            </div>
          ) : (
            phaseBlocks.map((block) => {
              const groupedByWeek = new Map<number, Card[]>();
              for (const card of block.cards) {
                const week = card.weekNumber ?? 0;
                const weekBucket = groupedByWeek.get(week) ?? [];
                weekBucket.push(card);
                groupedByWeek.set(week, weekBucket);
              }

              const orderedWeeks = [...groupedByWeek.keys()].sort((a, b) => a - b);

              return (
                <section key={block.phase} className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] overflow-hidden">
                  <div className="px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-2)]">
                    <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">{PHASE_LABEL[block.phase] ?? PHASE_LABEL.unknown}</span>
                    <span className="text-[12px] text-[var(--color-text-muted)] tabular-nums">{block.cards.length}</span>
                  </div>

                  <div className="divide-y divide-[var(--color-border-stroke)]">
                    {orderedWeeks.map((week) => (
                      <div key={`${block.phase}-${week}`}>
                        <div className="px-[var(--spacing-4)] py-[var(--spacing-2)] bg-[var(--color-bg-sidebar)] text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--color-text-muted)]">
                          {WEEK_LABEL[week] ?? `W${week}`}
                        </div>
                        <div className="divide-y divide-[var(--color-border-stroke)]">
                          {(groupedByWeek.get(week) ?? []).map((card) => {
                            const meta = getTaskMeta(card);
                            return (
                              <button
                                key={card.id}
                                onClick={() => onSelectCard(card.id)}
                                className={cn(
                                  "w-full text-left px-[var(--spacing-4)] py-[var(--spacing-2)]",
                                  "hover:bg-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)] cursor-pointer",
                                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-focus)]",
                                  "flex items-center gap-[var(--spacing-3)]",
                                )}
                              >
                                <PriorityDot priority={card.priority} />
                                <span className="text-[14px] text-[var(--color-text-primary)] flex-1 truncate">{card.title}</span>

                                {meta.executionWindow && (
                                  <span className="text-[11px] text-[var(--color-text-muted)] border border-[var(--color-border-stroke)] rounded-[var(--radius-sm)] px-[6px] py-[2px]">
                                    {meta.executionWindow.replace("_CET", " CET")}
                                  </span>
                                )}

                                <OwnerTag owner={card.owner} />
                                <StatusChip status={card.status} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
